/**
 * @qrqy/shared/urlValidator
 *
 * URL validation for QR redirect destinations.
 * All logic here is enforced at destination update time — NOT at redirect time.
 * This keeps the redirect Worker fast (no validation overhead on every scan).
 *
 * Rules are intentionally strict: we're redirecting people based on a physical
 * garment scan — the URL must be safe, stable, and scannable.
 *
 * This module has NO external dependencies and works in:
 *   - Cloudflare Workers runtime
 *   - Vite-bundled browser apps (dashboard)
 *   - Node.js (for testing)
 */

/** Maximum allowed destination URL length (characters). */
const MAX_URL_LENGTH = 2048;

/**
 * TLDs associated with free domain services historically used for spam/phishing.
 * This is a blocklist of last resort — legitimate URLs on these TLDs are very rare.
 * TODO(M3): Move to a KV-backed allowlist/blocklist that can be updated without code deploy.
 */
const BLOCKED_TLDS = new Set(['.tk', '.ml', '.ga', '.cf', '.gq']);

/**
 * Protocols that must never appear as QR redirect destinations.
 * These could execute code or exfiltrate data when a browser "opens" the URL.
 */
const BLOCKED_PROTOCOLS = new Set([
  'javascript:',
  'data:',
  'vbscript:',
  'file:',
  'ftp:',
]);

/**
 * RFC 1918 + loopback private IP ranges for isPrivateIP().
 * Encoded as { prefix: string, prefixLength: number } objects for CIDR matching.
 */
const PRIVATE_IP_PREFIXES = [
  // IPv4 loopback
  { cidr: '127.', bits: 8 },
  // IPv4 private
  { cidr: '10.', bits: 8 },
  { cidr: '172.16.', bits: 12 },
  { cidr: '172.17.', bits: 12 },
  { cidr: '172.18.', bits: 12 },
  { cidr: '172.19.', bits: 12 },
  { cidr: '172.20.', bits: 12 },
  { cidr: '172.21.', bits: 12 },
  { cidr: '172.22.', bits: 12 },
  { cidr: '172.23.', bits: 12 },
  { cidr: '172.24.', bits: 12 },
  { cidr: '172.25.', bits: 12 },
  { cidr: '172.26.', bits: 12 },
  { cidr: '172.27.', bits: 12 },
  { cidr: '172.28.', bits: 12 },
  { cidr: '172.29.', bits: 12 },
  { cidr: '172.30.', bits: 12 },
  { cidr: '172.31.', bits: 12 },
  { cidr: '192.168.', bits: 16 },
  // Link-local
  { cidr: '169.254.', bits: 16 },
  // IPv6 loopback and private
  { cidr: '::1', bits: 128 },
  { cidr: 'fc00:', bits: 7 },
  { cidr: 'fd', bits: 8 },
  { cidr: 'fe80:', bits: 10 },
];

/**
 * Check whether a hostname resolves to a private or loopback IP range.
 *
 * Note: This is a hostname-string check only — we cannot do DNS resolution
 * in the Workers runtime at URL-validation time without a network call.
 * Actual IP-based hostnames (e.g. "192.168.1.1") are caught here.
 * Hostnames that *resolve* to private IPs must be addressed via DNS filtering.
 *
 * @param {string} hostname - The hostname from a parsed URL
 * @returns {boolean} true if the hostname looks like a private IP
 */
export function isPrivateIP(hostname) {
  if (!hostname) return false;

  // Exact matches
  if (hostname === 'localhost' || hostname === '0.0.0.0' || hostname === '::1') {
    return true;
  }

  // Strip IPv6 bracket notation: [::1] → ::1
  const normalized = hostname.startsWith('[') && hostname.endsWith(']')
    ? hostname.slice(1, -1)
    : hostname;

  // Check against known private prefixes
  for (const { cidr } of PRIVATE_IP_PREFIXES) {
    if (normalized.startsWith(cidr)) return true;
  }

  // Check .local domains (mDNS / local network)
  if (normalized.endsWith('.local') || normalized.endsWith('.internal')) {
    return true;
  }

  return false;
}

/**
 * Trim and normalize a URL for validation and storage.
 * - Trims whitespace
 * - Lowercases the scheme (e.g. HTTP:// → http://)
 * - Does NOT lowercase the path/query (paths are case-sensitive)
 *
 * @param {string} url
 * @returns {string}
 */
export function sanitizeUrl(url) {
  if (!url || typeof url !== 'string') return '';

  const trimmed = url.trim();

  // Lowercase only the protocol portion
  const protocolMatch = trimmed.match(/^([a-zA-Z][a-zA-Z0-9+\-.]*:\/\/)/);
  if (protocolMatch) {
    return protocolMatch[1].toLowerCase() + trimmed.slice(protocolMatch[1].length);
  }

  // If no protocol, lowercase the start to normalize common mistakes
  // (e.g. "HTTPS://..." → "https://...")
  return trimmed;
}

/**
 * Validate a destination URL for use as a QR redirect target.
 *
 * @param {string} url - The destination URL to validate
 * @returns {{ valid: boolean, errors: string[] }}
 *
 * @example
 * validateDestinationUrl('https://example.com')
 * // → { valid: true, errors: [] }
 *
 * validateDestinationUrl('http://localhost')
 * // → { valid: false, errors: ['URL must use HTTPS.', 'Private and local addresses are not allowed.'] }
 */
export function validateDestinationUrl(url) {
  const errors = [];

  // Guard: empty
  if (!url || typeof url !== 'string') {
    return { valid: false, errors: ['URL is required.'] };
  }

  const trimmed = url.trim();

  // Guard: length
  if (trimmed.length > MAX_URL_LENGTH) {
    errors.push(`URL exceeds maximum length of ${MAX_URL_LENGTH} characters (got ${trimmed.length}).`);
    return { valid: false, errors };
  }

  // Guard: blocked protocols (checked on raw string before URL parsing)
  const lowerInput = trimmed.toLowerCase();
  for (const blocked of BLOCKED_PROTOCOLS) {
    if (lowerInput.startsWith(blocked)) {
      errors.push(`The protocol "${blocked}" is not allowed as a QR destination.`);
      return { valid: false, errors };
    }
  }

  // Parse with the URL constructor — the authoritative validity check
  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch {
    errors.push('Not a valid URL. Include the full address (e.g. https://example.com).');
    return { valid: false, errors };
  }

  // Must be HTTPS
  if (parsed.protocol !== 'https:') {
    errors.push('URL must use HTTPS. HTTP is not allowed — it may show browser warnings to scanners.');
  }

  // No private/local IPs or hostnames
  if (isPrivateIP(parsed.hostname)) {
    errors.push('Private and local addresses are not allowed (localhost, 192.168.x.x, etc.).');
  }

  // Blocked TLDs
  const hostname = parsed.hostname.toLowerCase();
  for (const tld of BLOCKED_TLDS) {
    if (hostname.endsWith(tld)) {
      errors.push(`Domains ending in "${tld}" are not accepted as QR destinations.`);
    }
  }

  // Must have a non-empty hostname
  if (!parsed.hostname || parsed.hostname.length === 0) {
    errors.push('URL must include a valid hostname.');
  }

  // Disallow bare IP addresses as hostnames (not private, but still undesirable for a fashion QR)
  // Allow: example.com, sub.example.com
  // Disallow: 93.184.216.34 (public IPs)
  const ipv4Pattern = /^\d{1,3}(\.\d{1,3}){3}$/;
  if (ipv4Pattern.test(parsed.hostname)) {
    errors.push('IP addresses are not allowed as QR destinations. Use a domain name.');
  }

  return { valid: errors.length === 0, errors };
}
